<script setup>
import { onBeforeUnmount, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTrack } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';
import { CONTACTS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useContactFilterContext } from './contactProvider.js';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

import Button from 'next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import FilterGroup from './FilterGroup.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  isSegmentView: { type: Boolean, default: false },
  segmentName: { type: String, default: '' },
});

const emit = defineEmits([
  'applyFilter',
  'updateSegment',
  'close',
  'clearFilters',
]);
const { filterTypes } = useContactFilterContext();

const filters = defineModel({
  type: Array,
  default: [],
});

const segmentNameLocal = ref(props.segmentName);
const { t } = useI18n();
const store = useStore();

const filterGroups = ref([]);
const copyPreviousGroup = ref(false);
const globalGroupOperator = ref('and');
const groupCounter = ref(0);

const createDefaultFilter = () => ({
  attributeKey: '',
  filterOperator: 'equal_to',
  values: [],
});

const nextGroupId = () => {
  groupCounter.value += 1;
  return groupCounter.value;
};

const createDefaultGroup = () => ({
  id: nextGroupId(),
  internalOperator: 'and',
  filters: [createDefaultFilter()],
  groupOperator: globalGroupOperator.value || 'and',
});

const cloneGroup = group => ({
  id: nextGroupId(),
  internalOperator: group.internalOperator,
  filters: group.filters.map(filter => ({
    ...filter,
    values: Array.isArray(filter.values) ? [...filter.values] : filter.values,
    queryOperator: filter.queryOperator || 'and',
  })),
  groupOperator: null,
});

const convertFiltersToGroups = flatFilters => {
  if (!flatFilters || flatFilters.length === 0) {
    return [createDefaultGroup()];
  }

  const hasGroupIndex = flatFilters.some(f => f.groupIndex !== undefined);

  if (hasGroupIndex) {
    const groupsMap = {};

    flatFilters.forEach(filter => {
      const idx = filter.groupIndex ?? 0;
      if (!groupsMap[idx]) {
        groupsMap[idx] = {
          id: nextGroupId(),
          internalOperator: filter.queryOperator || 'and',
          filters: [],
          groupOperator: null,
        };
      }
      groupsMap[idx].filters.push({
        attributeKey: filter.attributeKey,
        filterOperator: filter.filterOperator,
        values: Array.isArray(filter.values)
          ? [...filter.values]
          : filter.values,
        queryOperator: filter.queryOperator || 'and',
      });
    });

    const groupIndices = Object.keys(groupsMap)
      .map(Number)
      .sort((a, b) => a - b);
    const groups = groupIndices.map((idx, i) => {
      const group = groupsMap[idx];
      if (group.filters.length > 0) {
        group.internalOperator = group.filters[0].queryOperator || 'and';
      }

      if (i < groupIndices.length - 1) {
        const lastFilter = group.filters[group.filters.length - 1];
        group.groupOperator = lastFilter.queryOperator || 'or';
      }
      return group;
    });

    if (groups.length > 0) {
      const lastGroupIndex = groups.length - 1;
      const previousGroupOperator =
        lastGroupIndex > 0 ? groups[lastGroupIndex - 1].groupOperator : null;
      groups[lastGroupIndex].groupOperator =
        previousGroupOperator || globalGroupOperator.value || 'and';
    }

    if (groups.length > 1 && groups[0].groupOperator) {
      globalGroupOperator.value = groups[0].groupOperator;
    }

    return groups.length > 0 ? groups : [createDefaultGroup()];
  }

  const internalOperator = flatFilters[0]?.queryOperator || 'and';
  return [
    {
      id: nextGroupId(),
      internalOperator,
      filters: flatFilters.map(filter => ({
        attributeKey: filter.attributeKey,
        filterOperator: filter.filterOperator,
        values: Array.isArray(filter.values)
          ? [...filter.values]
          : filter.values,
        queryOperator: filter.queryOperator || 'and',
      })),
      groupOperator: null,
    },
  ];
};

const convertGroupsToFilters = groups => {
  const flatFilters = [];

  groups.forEach((group, groupIndex) => {
    group.filters.forEach((filter, filterIndex) => {
      if (!filter.attributeKey) return;

      let queryOperator = null;
      const isLastFilterInGroup = filterIndex === group.filters.length - 1;
      const isLastGroup = groupIndex === groups.length - 1;

      if (!isLastFilterInGroup) {
        queryOperator = group.internalOperator || 'and';
      } else if (!isLastGroup) {
        queryOperator = globalGroupOperator.value || 'or';
      }

      flatFilters.push({
        attributeKey: filter.attributeKey,
        filterOperator: filter.filterOperator,
        values: filter.values,
        queryOperator,
        groupIndex,
      });
    });
  });

  return flatFilters;
};

filterGroups.value = convertFiltersToGroups(filters.value);

watch(
  () => filters.value,
  newFilters => {
    filterGroups.value = convertFiltersToGroups(newFilters);
  },
  { deep: true }
);

const resetFilter = () => {
  filterGroups.value = [createDefaultGroup()];
  globalGroupOperator.value = 'or';
  emit('clearFilters');
};

const addGroup = () => {
  if (filterGroups.value.length > 0) {
    filterGroups.value[filterGroups.value.length - 1].groupOperator =
      globalGroupOperator.value;
  }

  if (copyPreviousGroup.value && filterGroups.value.length > 0) {
    const lastGroup = filterGroups.value[filterGroups.value.length - 1];
    const clonedGroup = cloneGroup(lastGroup);
    clonedGroup.groupOperator = globalGroupOperator.value;
    filterGroups.value.push(clonedGroup);
  } else {
    const newGroup = createDefaultGroup();
    newGroup.groupOperator = globalGroupOperator.value;
    filterGroups.value.push(newGroup);
  }
};

const updateGlobalGroupOperator = newOperator => {
  globalGroupOperator.value = newOperator;

  filterGroups.value.forEach(group => {
    group.groupOperator = newOperator;
  });
};

const removeGroup = groupIndex => {
  if (filterGroups.value.length === 1) {
    resetFilter();
  } else {
    filterGroups.value.splice(groupIndex, 1);

    filterGroups.value.forEach(group => {
      group.groupOperator = globalGroupOperator.value;
    });
  }
};

const isConditionsValid = () => {
  return filterGroups.value.every(group =>
    group.filters.every(filter => {
      if (!filter.attributeKey) return false;

      const filterType = filterTypes.value.find(
        ft => ft.attributeKey === filter.attributeKey
      );
      if (!filterType) return false;

      const operator = filterType.filterOperators?.find(
        op => op.value === filter.filterOperator
      );
      if (!operator) return false;

      if (!operator.hasInput) return true;

      const inputType = operator.inputOverride ?? filterType.inputType;

      if (inputType === 'multiSelect') {
        return Array.isArray(filter.values) && filter.values.length > 0;
      }
      if (['searchSelect', 'booleanSelect'].includes(inputType)) {
        return (
          filter.values &&
          typeof filter.values === 'object' &&
          filter.values.id !== undefined
        );
      }
      return (
        filter.values !== '' &&
        filter.values !== null &&
        filter.values !== undefined
      );
    })
  );
};

const canApplyFilters = computed(() => {
  return isConditionsValid();
});

const updateSavedSegment = () => {
  if (!canApplyFilters.value) return;
  const flatFilters = convertGroupsToFilters(filterGroups.value);
  emit('updateSegment', flatFilters, segmentNameLocal.value);
};

function validateAndSubmit() {
  if (!canApplyFilters.value) return;

  const flatFilters = convertGroupsToFilters(filterGroups.value);
  const payload = useSnakeCase(JSON.parse(JSON.stringify(flatFilters)));
  store.dispatch('contacts/setContactFilters', payload);
  emit('applyFilter', flatFilters);
  useTrack(CONTACTS_EVENTS.APPLY_FILTER, {
    appliedFilters: flatFilters.map(filter => ({
      key: filter.attributeKey,
      operator: filter.filterOperator,
      queryOperator: filter.queryOperator,
    })),
  });
}

const filterModalHeaderTitle = computed(() => {
  return !props.isSegmentView
    ? t('CONTACTS_LAYOUT.FILTER.TITLE')
    : t('CONTACTS_LAYOUT.FILTER.EDIT_SEGMENT');
});

onBeforeUnmount(() => emit('close'));

const outsideClickHandler = [
  () => emit('close'),
  {
    ignore: [
      '#toggleContactsFilterButton',
      '.filter-select-dropdown',
      '[data-filter-dropdown]',
      '.multiselect-dropdown',
      '[data-multiselect-dropdown]',
      '.singleselect-dropdown',
      '[data-singleselect-dropdown]',
    ],
  },
];
</script>

<template>
  <div
    v-on-click-outside="outsideClickHandler"
    class="z-40 max-w-3xl lg:w-[750px] w-full border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg rounded-xl flex flex-col max-h-[80vh]"
  >
    <!-- Header fixo -->
    <div class="p-6 pb-0 flex-shrink-0">
      <div class="flex items-center justify-between gap-4">
        <h3 class="text-base font-medium leading-6 text-n-slate-12">
          {{ filterModalHeaderTitle }}
        </h3>
      </div>

      <div v-if="props.isSegmentView" class="border-b border-n-weak pb-6 mt-6">
        <Input
          v-model="segmentNameLocal"
          :label="t('CONTACTS_LAYOUT.FILTER.SEGMENT.LABEL')"
          :placeholder="t('CONTACTS_LAYOUT.FILTER.SEGMENT.INPUT_PLACEHOLDER')"
        />
      </div>
    </div>

    <!-- Conteúdo com scroll -->
    <div class="flex-1 overflow-y-auto p-6 pt-4">
      <div class="grid gap-4">
        <template v-for="(group, groupIndex) in filterGroups" :key="group.id">
          <FilterGroup
            v-model:filters="group.filters"
            v-model:internal-operator="group.internalOperator"
            v-model:group-operator="group.groupOperator"
            :group-index="groupIndex"
            :is-only-group="filterGroups.length === 1"
            :filter-types="filterTypes"
            :show-operator="groupIndex > 0"
            :is-first-operator="groupIndex === 1"
            @remove="removeGroup(groupIndex)"
            @update:group-operator="
              groupIndex === 1 ? updateGlobalGroupOperator($event) : null
            "
          />
        </template>
      </div>
    </div>

    <!-- Footer fixo -->
    <div class="p-6 pt-0 flex-shrink-0">
      <div class="flex gap-2 justify-between pt-4 border-t border-n-weak">
        <div class="flex items-center gap-3">
          <Button sm ghost blue icon="i-lucide-layers" @click="addGroup">
            {{ t('CONTACTS_LAYOUT.FILTER.BUTTONS.ADD_FILTER_GROUP') }}
          </Button>
          <label
            class="flex items-center gap-2 text-xs text-n-slate-11 cursor-pointer"
          >
            <Switch v-model="copyPreviousGroup" size="small" />
            {{ t('CONTACTS_LAYOUT.FILTER.BUTTONS.COPY_PREVIOUS_GROUP') }}
          </label>
        </div>
        <div class="flex gap-2">
          <Button sm faded slate @click="resetFilter">
            {{ t('CONTACTS_LAYOUT.FILTER.BUTTONS.CLEAR_FILTERS') }}
          </Button>
          <Button
            v-if="isSegmentView"
            sm
            solid
            blue
            :disabled="!segmentNameLocal || !canApplyFilters"
            @click="updateSavedSegment"
          >
            {{ t('CONTACTS_LAYOUT.FILTER.BUTTONS.UPDATE_SEGMENT') }}
          </Button>
          <Button
            v-else
            sm
            solid
            blue
            :disabled="!canApplyFilters"
            @click="validateAndSubmit"
          >
            {{ t('CONTACTS_LAYOUT.FILTER.BUTTONS.APPLY_FILTERS') }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
